import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { FormsService } from '../../services/forms.service';
import { FormUtilities } from '../../utilities/form-utilities';
import { emptyAction } from '../actions/empty.actions';
import { cleanupFormList, cleanupFormListSuccess } from '../actions/form-list.actions';
import { abortFormSuccess, cleanupFormSuccess, submitFormSuccess } from '../actions/form.actions';
import { deleteLastOpenedForm } from '../actions/last-opened-form.actions';
import { cleanupSpqDocs } from '../actions/questionnaire.actions';
import { getLastOpenedFormByNotificationFactory } from '../selectors/last-opened-form.selector';
import { FormsState } from '../state/pace-forms.state';
import { cleanupSafetyAssessmentForm } from '../actions/safety-assessment.action';

// Effects which are taking care of deletion
@Injectable()
export class FormDeleteEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store<FormsState>,
        private readonly formsService: FormsService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    /**
     * When a form was either submitted or aborted, we the lock for that form will be released and therefore we can no
     * longer directly edit that form and can safely remove it from the last opened form state.
     */
    public deleteLastOpenedFormForIndividualForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(abortFormSuccess, submitFormSuccess),
            concatMap((action) =>
                of(action).pipe(
                    withLatestFrom(
                        this.store.select(
                            getLastOpenedFormByNotificationFactory({
                                notification: action.notification,
                            }),
                        ),
                    ),
                ),
            ),
            map(([action, lastOpenedForm]) =>
                typeof lastOpenedForm !== 'undefined' && FormUtilities.compare(action.form, lastOpenedForm)
                    ? deleteLastOpenedForm({ notification: action.notification })
                    : emptyAction(),
            ),
            catchError((error) => {
                this.log.error(
                    `FormEffects: deleteLastOpenedFormForIndividualForm$: error in pipeline: ${JSON.stringify(error)}`,
                );
                return EMPTY;
            }),
        ),
    );

    /**
     * When a form list was cleaned up, we know that this notification will no longer be used for opening a form. There-
     * fore we can now safely remove the last opened form for that notification from our state.
     */
    public deleteLastOpenedFormForFormList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cleanupFormListSuccess),
            concatMap((action) =>
                of(action).pipe(
                    withLatestFrom(
                        this.store.select(
                            getLastOpenedFormByNotificationFactory({
                                notification: action.notification,
                            }),
                        ),
                    ),
                ),
            ),
            map(([action, lastOpenedForm]) =>
                typeof lastOpenedForm !== 'undefined'
                    ? deleteLastOpenedForm({ notification: action.notification })
                    : emptyAction(),
            ),
            catchError((error) => {
                this.log.error(
                    `FormEffects: deleteLastOpenedFormForFormList$: error in pipeline: ${JSON.stringify(error)}`,
                );
                return EMPTY;
            }),
        ),
    );

    public cleanupFormList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cleanupFormList),
            mergeMap((action) => this.formsService.cleanupFormList(action.formList)),
            switchMap((formList) => {
                const entryCleanupActions = formList.entries.map((entry) =>
                    cleanupFormSuccess({
                        form: entry.id,
                        notification: formList.notification,
                        cached: entry.cached,
                    }),
                );

                return [...entryCleanupActions, cleanupFormListSuccess({ notification: formList.notification })];
            }),
            catchError((error) => {
                this.log.error(`FormEffects: cleanupFormList$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public cleanupSpqDocs$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cleanupSpqDocs),
                switchMap((action) =>
                    this.formsService.removeSpqFormFromCache(action.spqMaterials).pipe(
                        catchError((error) => {
                            this.log.error(`FormEffects: cleanupSpqDocs$: error in pipeline: ${JSON.stringify(error)}`);
                            return EMPTY;
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public cleanupSafDocs$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cleanupSafetyAssessmentForm),
                switchMap((action) =>
                    this.formsService.removeSafetyAssessmentFormFromCache(action.form, action.notification).pipe(
                        catchError((error) => {
                            this.log.error(`FormEffects: cleanupSafDocs$: error in pipeline: ${JSON.stringify(error)}`);
                            return EMPTY;
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );
}
